import React, { useRef, useState } from "react";
import { FileText, Upload, X } from "lucide-react";

const COLOR_VARIANTS = {
  "standart-ocean": {
    fileIcon: "bg-standart-ocean/15 text-standart-ocean",
    removeButton: "text-standart-ocean hover:bg-standart-ocean/10",
    dragging: "border-standart-ocean bg-standart-ocean/10",
    idle: "border-gray-200 bg-gray-50 hover:border-standart-ocean hover:bg-standart-ocean/5",
    uploadIcon: "bg-standart-ocean/10 text-standart-ocean",
  },
  "navy-blue": {
    fileIcon: "bg-navy-blue/15 text-navy-blue",
    removeButton: "text-navy-blue hover:bg-navy-blue/10",
    dragging: "border-navy-blue bg-navy-blue/10",
    idle: "border-gray-200 bg-gray-50 hover:border-navy-blue hover:bg-navy-blue/5",
    uploadIcon: "bg-navy-blue/10 text-navy-blue",
  },
};

const LAYOUT_VARIANTS = {
  default: {
    button: "px-5 py-8 flex-col",
    uploadIcon: "w-12 h-12 rounded-2xl",
    labels: "flex flex-col items-center gap-2.5 mt-2.5",
  },
  compact: {
    button: "px-4 py-4 flex-row gap-3 text-left",
    uploadIcon: "w-10 h-10 rounded-xl shrink-0",
    labels: "flex min-w-0 flex-1 flex-col items-start gap-1",
  },
};

const isFileAccepted = (selectedFile, accept) => {
  if (!accept) return true;
  const fileName = selectedFile.name.toLowerCase();
  const fileType = selectedFile.type.toLowerCase();
  return accept.split(",").some((acceptedType) => {
    const normalizedAcceptedType = acceptedType.trim().toLowerCase();
    if (normalizedAcceptedType.startsWith(".")) return fileName.endsWith(normalizedAcceptedType);
    if (normalizedAcceptedType.endsWith("/*")) return fileType.startsWith(normalizedAcceptedType.slice(0, -1));
    return fileType === normalizedAcceptedType;
  });
};

const FileUpload = ({ accept, fileName, onFileSelect, onFileClear, prompt, hint, removeFileTitle, fileTypeError = "File type is not supported.", fileSizeError = "File is too large.", colorVariant = "standart-ocean", layoutVariant = "default", maxFileSizeBytes, isDisabled = false }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState("");
  const colorClasses = COLOR_VARIANTS[colorVariant] || COLOR_VARIANTS["standart-ocean"];
  const layoutClasses = LAYOUT_VARIANTS[layoutVariant] || LAYOUT_VARIANTS.default;
  const selectFile = (selectedFile) => {
    if (!selectedFile) return;
    if (!isFileAccepted(selectedFile, accept)) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setValidationError("fileType");
      return;
    }
    if (maxFileSizeBytes && selectedFile.size > maxFileSizeBytes) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setValidationError("fileSize");
      return;
    }
    setValidationError("");
    onFileSelect(selectedFile);
  };
  const clearFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    setValidationError("");
    onFileClear();
  };
  return (
    <div className={isDisabled ? "opacity-60 pointer-events-none" : ""}>
      <input ref={fileInputRef} type="file" accept={accept} className="hidden" disabled={isDisabled} onChange={(event) => selectFile(event.target.files?.[0])} />
      {fileName ? (
        <div className="flex items-center gap-3 px-[18px] py-4 bg-[#eef3f8] rounded-2xl">
          <span className={`w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0 ${colorClasses.fileIcon}`}><FileText size={20} strokeWidth={2.2} /></span>
          <span className="flex-1 min-w-0 font-bold text-sm text-navy-blue truncate">{fileName}</span>
          <button type="button" title={removeFileTitle} onClick={clearFile} disabled={isDisabled} className={`w-9 h-9 rounded-full inline-flex items-center justify-center transition shrink-0 ${colorClasses.removeButton}`}><X size={16} /></button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(event) => { event.preventDefault(); setIsDragging(false); selectFile(event.dataTransfer.files?.[0]); }}
          disabled={isDisabled}
          className={`w-full rounded-[18px] border-2 border-dashed cursor-pointer flex items-center transition ${layoutClasses.button} ${isDragging ? colorClasses.dragging : colorClasses.idle}`}
        >
          <span className={`inline-flex items-center justify-center ${layoutClasses.uploadIcon} ${colorClasses.uploadIcon}`}><Upload size={22} strokeWidth={2.2} /></span>
          <span className={layoutClasses.labels}>
            <span className="font-bold text-[15px] text-navy-blue">{prompt}</span>
            <span className="text-[13px] text-gray-400">{hint}</span>
          </span>
        </button>
      )}
      {validationError && <p role="alert" className="text-red-500 text-xs text-left mt-1.5">{validationError === "fileType" ? fileTypeError : fileSizeError}</p>}
    </div>
  );
};

export default FileUpload;
